import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { projectApiUrl } from "../../constants";

/** Resolve on demand: never reuse a persisted presigned URL for KYC evidence. */
export default function VerificationDocument({ fileId, fileName }: { fileId: string; fileName: string }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const resolveDocument = async () => {
    // Open synchronously with the user's click so browsers do not block the
    // signed URL after the asynchronous authorization request.
    const preview = window.open("about:blank", "_blank");
    if (preview) {
      preview.opener = null;
      const policy = preview.document.createElement("meta");
      policy.name = "referrer";
      policy.content = "no-referrer";
      preview.document.head.appendChild(policy);
    }
    setLoading(true);
    setError(false);
    setUrl(null);
    try {
      const response = await axios.get(
        `${projectApiUrl}/personal_data/verification/documents/${encodeURIComponent(fileId)}/download-url`,
        { headers: { "X-Requested-With": "XMLHttpRequest" }, withCredentials: true },
      );
      if (response.status !== 200 || typeof response.data?.downloadUrl !== "string") {
        preview?.close();
        setError(true);
      } else {
        const parsed = new URL(response.data.downloadUrl);
        if (parsed.protocol !== "https:") throw new Error("Invalid document URL");
        if (preview && !preview.closed) preview.location.replace(parsed.href);
        else setUrl(parsed.href);
      }
    } catch {
      preview?.close();
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return <div>
    <span>{fileName}</span>{" "}
    <button type="button" disabled={loading} onClick={resolveDocument}>
      {t(loading ? "personalData.accountVerification.documentLoading" : "personalData.accountVerification.viewDocument")}
    </button>
    {url && <a href={url} target="_blank" rel="noreferrer" onClick={() => setUrl(null)}>
      {t("personalData.accountVerification.viewDocument")}
    </a>}
    {error && <span role="alert">{t("personalData.accountVerification.documentUnavailable")}</span>}
  </div>;
}
